import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchTranslationGaps,
  runMissingTranslations,
  TRANSLATABLE_TABLES,
  TRANSLATION_LANGS,
  type MissingTranslationsProgress,
  type RunMissingTranslationsSummary,
  type TranslatableTable,
  type TranslationGap,
} from '../lib/referentialService';
import { logger } from '../lib/logger';

interface Props {
  onClose: () => void;
  /** Appelé à la fin d'une passe (succès, stop ou erreur) pour rafraîchir les indicateurs. */
  onDone?: () => void;
}

/**
 * Passe globale « Compléter les traductions » : affiche les manques par
 * table × langue, puis lance la traduction (mode onlyMissing) avec progression,
 * bouton stop et journal des erreurs réelles.
 */
const MissingTranslationsModal = ({ onClose, onDone }: Props) => {
  const [gaps, setGaps] = useState<TranslationGap[] | null>(null);
  const [gapsError, setGapsError] = useState<string | null>(null);
  const [loadingGaps, setLoadingGaps] = useState(false);

  const [selectedTables, setSelectedTables] = useState<Set<TranslatableTable>>(
    () => new Set(TRANSLATABLE_TABLES.map((t) => t.key)),
  );
  const [selectedLangs, setSelectedLangs] = useState<Set<string>>(
    () => new Set(TRANSLATION_LANGS.map((l) => l.key)),
  );

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<MissingTranslationsProgress | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [summary, setSummary] = useState<RunMissingTranslationsSummary | null>(null);
  const [stats, setStats] = useState({ translated: 0, skipped: 0, failed: 0 });
  const [stopping, setStopping] = useState(false);
  const stopRef = useRef(false);

  const loadGaps = useCallback(async () => {
    setLoadingGaps(true);
    setGapsError(null);
    try {
      setGaps(await fetchTranslationGaps());
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setGapsError(msg);
      logger.error('load_translation_gaps', 'MissingTranslationsModal', { error: msg });
    } finally {
      setLoadingGaps(false);
    }
  }, []);

  useEffect(() => { loadGaps(); }, [loadGaps]);

  const gapMatrix = useMemo(() => {
    const m: Record<string, Record<string, number>> = {};
    for (const g of gaps || []) {
      (m[g.table] ||= {})[g.lang] = g.missing;
    }
    return m;
  }, [gaps]);

  const rowTotal = (table: TranslatableTable) =>
    TRANSLATION_LANGS.reduce((acc, l) => acc + (gapMatrix[table]?.[l.key] || 0), 0);

  /** Nombre de cellules (item × langue) à produire dans le périmètre sélectionné. */
  const selectedCells = useMemo(() => {
    let n = 0;
    for (const t of selectedTables) {
      for (const l of selectedLangs) n += gapMatrix[t]?.[l] || 0;
    }
    return n;
  }, [gapMatrix, selectedTables, selectedLangs]);

  const toggle = <T,>(set: Set<T>, value: T, setter: (s: Set<T>) => void) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value); else next.add(value);
    setter(next);
  };

  const start = async () => {
    if (running || selectedTables.size === 0 || selectedLangs.size === 0) return;
    stopRef.current = false;
    setStopping(false);
    setRunning(true);
    setErrors([]);
    setSummary(null);
    setStats({ translated: 0, skipped: 0, failed: 0 });
    setProgress(null);

    const tables = TRANSLATABLE_TABLES.map((t) => t.key).filter((k) => selectedTables.has(k));
    const allLangs = selectedLangs.size === TRANSLATION_LANGS.length;
    const langs = allLangs ? undefined : TRANSLATION_LANGS.map((l) => l.key).filter((k) => selectedLangs.has(k));

    logger.functional('missing_translations_start', 'MissingTranslationsModal', { tables, langs: langs || 'all', cells: selectedCells });
    try {
      const result = await runMissingTranslations({
        tables,
        langs,
        shouldStop: () => stopRef.current,
        onError: (msg) => setErrors((prev) => (prev.length >= 200 ? prev : [...prev, msg])),
        onProgress: (p) => {
          setStats({ translated: p.totalTranslated, skipped: p.totalSkipped, failed: p.totalFailed });
          setProgress(p);
        },
      });
      setSummary(result);
      setStats({ translated: result.translated, skipped: result.skipped, failed: result.failed });
      logger.functional('missing_translations_done', 'MissingTranslationsModal', {
        translated: result.translated, skipped: result.skipped, failed: result.failed, stopped: result.stopped, errors: result.errors.length,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrors((prev) => [...prev, `Erreur fatale : ${msg}`]);
      logger.error('missing_translations_fatal', 'MissingTranslationsModal', { error: msg });
    } finally {
      setRunning(false);
      await loadGaps();
      onDone?.();
    }
  };

  const pct = progress && progress.chunkTotal > 0
    ? Math.round((progress.chunkDone / progress.chunkTotal) * 100)
    : 0;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={running ? undefined : onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Compléter les traductions</h3>
            <p className="text-xs text-gray-500">
              Traduit uniquement les libellés manquants ({TRANSLATION_LANGS.length} langues cibles). Les traductions existantes et verrouillées sont conservées.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={running}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 disabled:opacity-40"
            title={running ? 'Arrêtez la passe avant de fermer' : 'Fermer'}
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Matrice des manques */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-600 uppercase">Libellés manquants par table et langue</span>
              <button
                onClick={loadGaps}
                disabled={loadingGaps || running}
                className="text-xs text-[#00244F] hover:underline disabled:opacity-40"
              >
                {loadingGaps ? 'Actualisation…' : 'Actualiser'}
              </button>
            </div>
            {gapsError ? (
              <div className="px-4 py-6 text-sm text-red-600">
                Impossible de charger les manques : {gapsError}
                <div className="text-xs text-gray-500 mt-1">La migration 077 (RPC <code>referential_translation_gaps</code>) est-elle appliquée ?</div>
              </div>
            ) : !gaps ? (
              <div className="px-4 py-6 text-sm text-gray-400 text-center">Chargement…</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-white text-gray-500">
                      <th className="px-3 py-2 text-left font-medium">
                        <span className="sr-only">Table</span>
                      </th>
                      {TRANSLATION_LANGS.map((l) => (
                        <th key={l.key} className="px-2 py-2 font-medium text-center">
                          <label className="inline-flex flex-col items-center gap-1 cursor-pointer select-none" title={l.label}>
                            <input
                              type="checkbox"
                              checked={selectedLangs.has(l.key)}
                              disabled={running}
                              onChange={() => toggle(selectedLangs, l.key, setSelectedLangs)}
                              className="rounded border-gray-300 text-[#00244F] focus:ring-[#00244F]/20"
                            />
                            <span className="uppercase">{l.key}</span>
                          </label>
                        </th>
                      ))}
                      <th className="px-3 py-2 font-medium text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TRANSLATABLE_TABLES.map((t) => {
                      const total = rowTotal(t.key);
                      return (
                        <tr key={t.key} className={`border-t border-gray-100 ${selectedTables.has(t.key) ? '' : 'opacity-50'}`}>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={selectedTables.has(t.key)}
                                disabled={running}
                                onChange={() => toggle(selectedTables, t.key, setSelectedTables)}
                                className="rounded border-gray-300 text-[#00244F] focus:ring-[#00244F]/20"
                              />
                              <span className="text-gray-800 font-medium">{t.label}</span>
                            </label>
                          </td>
                          {TRANSLATION_LANGS.map((l) => {
                            const n = gapMatrix[t.key]?.[l.key] || 0;
                            return (
                              <td key={l.key} className={`px-2 py-1.5 text-center tabular-nums ${n > 0 ? 'text-orange-600 font-medium' : 'text-gray-300'}`}>
                                {n > 0 ? n.toLocaleString('fr-FR') : '·'}
                              </td>
                            );
                          })}
                          <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${total > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
                            {total.toLocaleString('fr-FR')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Lancement */}
          <div className="flex items-center gap-3">
            <button
              onClick={start}
              disabled={running || !gaps || selectedTables.size === 0 || selectedLangs.size === 0 || selectedCells === 0}
              className="px-4 py-2.5 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {running ? 'Traduction en cours…' : selectedCells === 0 && gaps ? 'Rien à traduire' : `Lancer (${selectedCells.toLocaleString('fr-FR')} libellés à produire)`}
            </button>
            {running && (
              <button
                onClick={() => { stopRef.current = true; setStopping(true); }}
                disabled={stopping}
                className="px-4 py-2.5 text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 rounded-lg disabled:opacity-40"
              >
                {stopping ? 'Arrêt après les lots en cours…' : 'Stop'}
              </button>
            )}
            <p className="text-xs text-gray-500">
              Lots de 9 éléments, 3 en parallèle. Reprise automatique sur ce qui manque encore.
            </p>
          </div>

          {/* Progression */}
          {(progress || summary) && (
            <div className="border border-blue-200 bg-blue-50 rounded-xl px-4 py-3 space-y-2">
              <div className="flex items-center justify-between text-sm text-blue-900">
                <span className="font-medium">
                  {summary
                    ? summary.stopped
                      ? 'Arrêté par l’utilisateur.'
                      : 'Terminé.'
                    : progress?.message}
                </span>
                {progress && !summary && (
                  <span className="text-xs tabular-nums">{pct}%</span>
                )}
              </div>
              {progress && !summary && (
                <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#00244F] transition-all" style={{ width: `${pct}%` }} />
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-white/70 px-2 py-1.5">
                  <div className="text-lg font-bold text-emerald-700 tabular-nums">{stats.translated.toLocaleString('fr-FR')}</div>
                  <div className="text-[11px] text-gray-600">éléments traduits</div>
                </div>
                <div className="rounded-lg bg-white/70 px-2 py-1.5">
                  <div className="text-lg font-bold text-gray-600 tabular-nums">{stats.skipped.toLocaleString('fr-FR')}</div>
                  <div className="text-[11px] text-gray-600">déjà complets</div>
                </div>
                <div className="rounded-lg bg-white/70 px-2 py-1.5">
                  <div className={`text-lg font-bold tabular-nums ${stats.failed > 0 ? 'text-red-600' : 'text-gray-400'}`}>{stats.failed.toLocaleString('fr-FR')}</div>
                  <div className="text-[11px] text-gray-600">en échec</div>
                </div>
              </div>
            </div>
          )}

          {/* Erreurs */}
          {errors.length > 0 && (
            <div className="border border-red-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-xs font-medium text-red-700 uppercase">
                Erreurs ({errors.length}{errors.length >= 200 ? '+' : ''})
              </div>
              <div className="max-h-44 overflow-y-auto divide-y divide-gray-100">
                {errors.map((e, i) => (
                  <div key={i} className="px-4 py-1.5 text-xs text-gray-700 font-mono break-all">{e}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MissingTranslationsModal;
